import {
  MatchDetailsResponseDto,
  MatchListEntitiesResponseDto,
} from "@/lib/api/interfaces/matches.interface";
import {
  useMatchesDelete,
  useMatchesFindOneQuery as useMatchesFindOne,
  useMatchesUpdate,
  useMatchesCreationEntitiesQuery,
} from "@/lib/api/requests/matches.requests";
import { formatDate, formatDateTimeForForm } from "@/lib/utils/date-formatter";
import { Button } from "@/ui-components/ui/button";
import { Autocomplete } from "@/util-components/inputs/autocomplete";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/ui-components/ui/form";
import { Input } from "@/ui-components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calendar,
  Loader2,
  MapPin,
  Radio,
  Shield,
  Swords,
  Trophy,
  Megaphone,
  Clock,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "@/lib/toast";
import z from "zod";
import { getMatchStatus } from "./page";
import { Skeleton } from "@/ui-components/ui/skeleton";
import { Badge } from "@/ui-components/ui/badge";
import { EntitySelector } from "./components/entity-selector";
import CreateTeamDialog from "../teams/create-dialog";
import CreateOpponentDialog from "./opponents/create-dialog";
import { Tabs, TabsList, TabsTrigger } from "@/ui-components/ui/tabs";
import { cn } from "@/lib/utils/cn";
import { DefaultTemplateType } from "../post-generator/template-editor/registry/defaultTemplates";
import { MissingTemplateDialog } from "../../util-components/dialog/MissingTemplateDialog";
import { useDefaultTemplate } from "@/lib/hooks/use-default-template";

import { DetailsBaseDialog, DetailsBaseDialogMode, DialogContentItem, DialogContentSection, DialogHeaderSection, DialogMetaSection, DialogFormSection } from "@/util-components/dialog/base-dialogs";

/**
 * Main dialog component for viewing and editing match details.
 * Also handles deletion and data fetching logic via BaseDialog.
 */
const MatchDetailsDialog = ({
  open,
  onClose,
  matchId,
  listEntities,
  mode = "details",
}: {
  open: boolean;
  onClose: (refetchData: boolean) => void;
  matchId: string;
  listEntities?: MatchListEntitiesResponseDto;
  mode?: DetailsBaseDialogMode;
}) => {
  const [isDeleteInProgress, setIsDeleteInProgress] = useState(false);
  const { data: match, isLoading } = useMatchesFindOne(matchId, {
    enabled: open && !!matchId && !isDeleteInProgress,
  });
  const { mutate: deleteMatch, isPending: isDeleting } = useMatchesDelete();

  const [isSaving, setIsSaving] = useState(false);
  const [canSave, setCanSave] = useState(false);

  if (!match && !isLoading) {
    return (
      <DetailsBaseDialog
        open={open}
        onClose={onClose}
        icon={Swords}
        title="Spieldetails"
        description="Informationen zum Spiel einsehen und bearbeiten."
        deleteTitle="Spiel löschen"
        deleteItemTitle=""
        onDeleteConfirm={() => {}}
        detailsContent={
          <div className="py-12 text-center text-muted-foreground">
            Spiel nicht gefunden.
          </div>
        }
        editContent={() => null}
      />
    );
  }

  const handleDelete = async () => {
    if (!matchId || isDeleteInProgress) return;
    setIsDeleteInProgress(true);

    return new Promise<void>((resolve, reject) => {
      deleteMatch(
        { id: matchId },
        {
          onSuccess: () => {
            toast.success("Spiel wurde erfolgreich gelöscht");
            onClose(true);
            resolve();
          },
          onError: (error) => {
            toast.error("Spiel konnte nicht gelöscht werden", { error });
            setIsDeleteInProgress(false);
            reject();
          },
        }
      );
    });
  };

  return (
    <DetailsBaseDialog
      open={open}
      onClose={onClose}
      mode={mode}
      icon={Swords}
      title="Spieldetails"
      description="Informationen zum Spiel einsehen und bearbeiten."
      deleteItemTitle={`${match?.opponent?.name}`}
      deleteItemTypeValue={matchId}
      onDeleteConfirm={handleDelete}
      isDeleting={isDeleting || isDeleteInProgress}
      isSaving={isSaving}
      canSave={canSave}
      formId="edit-match-form"
      detailsContent={isLoading ? <MatchDetailsSkeleton /> : <MatchDetails match={match!} listEntities={listEntities} />}
      editContent={(onSaveSuccess) => (
        isLoading ? (
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <MatchDetailsSkeleton />
          </div>
        ) : (
          <MatchEditFormWrapper
            match={match!}
            listEntities={listEntities}
            onSaveSuccess={onSaveSuccess}
            setIsSaving={setIsSaving}
            setCanSave={setCanSave}
          />
        )
      )}
    />
  );
};

/**
 * Wrapper for the match edit form that ensures required creation entities (e.g. available teams)
 * are fetched before rendering the form.
 */
const MatchEditFormWrapper = ({
  match,
  listEntities,
  onSaveSuccess,
  setIsSaving,
  setCanSave,
}: {
  match: MatchDetailsResponseDto;
  listEntities?: MatchListEntitiesResponseDto;
  onSaveSuccess: () => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const {
    data: creationEntities,
    isLoading: isLoadingEntities,
    refetch,
  } = useMatchesCreationEntitiesQuery({
    enabled: true,
  });

  if (isLoadingEntities) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <MatchEditForm
      onSaveSuccess={onSaveSuccess}
      match={match}
      listEntities={creationEntities}
      refetchEntities={refetch}
      setIsSaving={setIsSaving}
      setCanSave={setCanSave}
    />
  );
};

/**
 * Displays the skeleton loader while match details are fetching.
 */
const MatchDetailsSkeleton = () => {
  return (
    <div className="space-y-6 overflow-y-auto">
      <div className="grid grid-cols-3 gap-4 text-center items-center py-6 bg-gray-50 rounded-xl">
        <Skeleton className="h-6 w-24 mx-auto" />
        <Skeleton className="h-8 w-16 mx-auto" />
        <Skeleton className="h-6 w-24 mx-auto" />
      </div>
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Skeleton className="h-20 w-full rounded-xl" />
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
};

/**
 * Read-only view for a match's details including the scoreboard,
 * location info, and social media post shortcuts.
 */
const MatchDetails = ({
  match,
  listEntities,
}: {
  match: MatchDetailsResponseDto;
  listEntities?: MatchListEntitiesResponseDto;
}) => {
  const matchStatus = getMatchStatus(match);
  const isHomeGame = match.homeOrAway !== "AWAY";

  const homeTeamName = isHomeGame ? match.team?.name : match.opponent?.name;
  const homeTeamLogo = isHomeGame
    ? match.team?.thumbnailUrl || match.team?.logoUrl
    : match.opponent?.thumbnailUrl || match.opponent?.logoUrl;

  const awayTeamName = isHomeGame ? match.opponent?.name : match.team?.name;
  const awayTeamLogo = isHomeGame
    ? match.opponent?.thumbnailUrl || match.opponent?.logoUrl
    : match.team?.thumbnailUrl || match.team?.logoUrl;

  const homeScore = isHomeGame ? match.ourScore : match.opponentScore;
  const awayScore = isHomeGame ? match.opponentScore : match.ourScore;

  const hasScore =
    homeScore !== null &&
    homeScore !== undefined &&
    awayScore !== null &&
    awayScore !== undefined;

  const {
    handleCreatePost,
    isLoading: isLoadingDefaults,
    missingTemplateDialogProps,
  } = useDefaultTemplate({
    prefillData: {
      entityType: "Match",
      entityId: match._id,
    },
  });

  const onHandleCreatePost = (type: DefaultTemplateType, label: string) => {
    // If the template is assigned, we want to close the details dialog before navigating
    // But handleCreatePost handles navigation immediately.
    // We can wrap it.
    // Actually, handleCreatePost doesn't return anything.
    // But useDefaultTemplate exposes `isAssigned`.

    // Better: handleCreatePost will navigate if assigned.
    // We can just call it. But we want to close this dialog too?
    // If we navigate away, this dialog unmounts anyway (if it's a page navigation).
    // Yes, handleCreatePost uses navigate() which changes the route.
    // But this dialog might be on the same route (if it's just a state dialog).
    // If we navigate to /post-generator, we leave the current page. So the dialog closes.
    // So simple call is fine.

    handleCreatePost(type, label);
  };

  return (
    <>
        {/* Missing Template Dialog */}
        <MissingTemplateDialog {...missingTemplateDialogProps} />

        {/* Scoreboard Header - Modern Gradient Design */}
        <DialogHeaderSection className="p-4 sm:p-8">
          <div className="flex items-start justify-between gap-2 sm:items-center sm:gap-4">
            {/* Home Team */}
            <div className="min-w-0 flex-1 flex flex-col items-center text-center">
              <div className="relative mb-2.5 flex h-16 w-16 items-center justify-center rounded-xl bg-white p-2.5 shadow-md sm:mb-3 sm:h-20 sm:w-20 sm:rounded-2xl sm:p-3">
                {homeTeamLogo ? (
                  <img
                    src={homeTeamLogo}
                    alt={homeTeamName || "Unbekannt"}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Shield className="h-8 w-8 text-gray-200 sm:h-10 sm:w-10" />
                )}
                <Badge
                  variant="secondary"
                  className="absolute -bottom-1.5 px-1.5 py-0 text-[8px] bg-white text-blue-700 border-blue-100 shadow-sm uppercase font-bold tracking-tighter pointer-events-none sm:-bottom-2 sm:px-2 sm:text-[9px]"
                >
                  Heim
                </Badge>
              </div>
              <p className="flex min-h-[2rem] items-center justify-center px-1 text-center text-xs font-bold leading-tight text-white line-clamp-2 sm:min-h-[2.5rem] sm:text-sm">
                {homeTeamName || "Unbekannt"}
              </p>
            </div>

            {/* Score / VS Area */}
            <div className="flex shrink-0 flex-col items-center px-1 sm:px-4">
              <div className="flex flex-col items-center rounded-xl border border-white/10 bg-black/20 px-3 py-2 shadow-inner backdrop-blur-md sm:rounded-2xl sm:px-6 sm:py-3">
                {hasScore ? (
                  <div className="flex items-center gap-2 sm:gap-3">
                    <span className="text-2xl font-black tracking-tighter text-white drop-shadow-sm sm:text-4xl">
                      {homeScore}
                    </span>
                    <span className="text-base font-medium text-white/40 sm:text-xl">:</span>
                    <span className="text-2xl font-black tracking-tighter text-white drop-shadow-sm sm:text-4xl">
                      {awayScore}
                    </span>
                  </div>
                ) : (
                  <span className="text-lg font-black tracking-[0.2em] text-white/90 sm:text-2xl sm:tracking-widest">
                    VS
                  </span>
                )}
              </div>
              <div className="mt-3 flex flex-col items-center gap-1.5 sm:mt-4 sm:gap-2">
                <Badge
                  className={cn(
                    "px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border-none shadow-sm pointer-events-none sm:px-3 sm:text-[10px]",
                    matchStatus.color
                  )}
                >
                  {matchStatus.text}
                </Badge>
              </div>
            </div>

            {/* Away Team */}
            <div className="min-w-0 flex-1 flex flex-col items-center text-center">
              <div className="relative mb-2.5 flex h-16 w-16 items-center justify-center rounded-xl bg-white p-2.5 shadow-md sm:mb-3 sm:h-20 sm:w-20 sm:rounded-2xl sm:p-3">
                {awayTeamLogo ? (
                  <img
                    src={awayTeamLogo}
                    alt={awayTeamName || "Unbekannt"}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <Shield className="h-8 w-8 text-gray-200 sm:h-10 sm:w-10" />
                )}
                <Badge
                  variant="secondary"
                  className="absolute -bottom-1.5 px-1.5 py-0 text-[8px] bg-white text-blue-700 border-blue-100 shadow-sm uppercase font-bold tracking-tighter pointer-events-none sm:-bottom-2 sm:px-2 sm:text-[9px]"
                >
                  Gast
                </Badge>
              </div>
              <p className="flex min-h-[2rem] items-center justify-center px-1 text-center text-xs font-bold leading-tight text-white line-clamp-2 sm:min-h-[2.5rem] sm:text-sm">
                {awayTeamName || "Unbekannt"}
              </p>
            </div>
          </div>
        </DialogHeaderSection>

        {/* Details Section */}
        <DialogContentSection
          title="Informationen"
          icon={Radio}
          rightElement={
            match.leagueName ? (
              <Badge
                variant="outline"
                className="border-blue-100 bg-blue-50/50 text-blue-700 text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-tight pointer-events-none"
              >
                {match.leagueName}
              </Badge>
            ) : null
          }
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <DialogContentItem
              label="Datum & Zeit"
              value={formatDate(match.date)}
              icon={Calendar}
            />
            <DialogContentItem
              label="Spielort"
              value={match.location || "Nicht angegeben"}
              icon={MapPin}
            />
          </div>
        </DialogContentSection>

        {/* Social Media Shortcuts */}
        <DialogContentSection title="Social Media" icon={Megaphone}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col gap-1 items-center justify-center border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all"
              onClick={() =>
                onHandleCreatePost(
                  DefaultTemplateType.MATCH_ANNOUNCEMENT,
                  "Spiel-Ankündigung"
                )
              }
              disabled={isLoadingDefaults}
            >
              <Megaphone className="w-5 h-5 mb-1" />
              <span className="font-bold text-xs">Ankündigung</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col gap-1 items-center justify-center border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all"
              onClick={() =>
                onHandleCreatePost(
                  DefaultTemplateType.MATCH_HALFTIME,
                  "Zwischenstand"
                )
              }
              disabled={isLoadingDefaults}
            >
              <Clock className="w-5 h-5 mb-1" />
              <span className="font-bold text-xs">Zwischenstand</span>
            </Button>
            <Button
              variant="outline"
              className="h-auto py-3 flex flex-col gap-1 items-center justify-center border-gray-200 hover:bg-blue-50 hover:border-blue-200 hover:text-blue-700 transition-all"
              onClick={() =>
                onHandleCreatePost(DefaultTemplateType.MATCH_RECAP, "Endstand")
              }
              disabled={isLoadingDefaults}
            >
              <Trophy className="w-5 h-5 mb-1" />
              <span className="font-bold text-xs">Endstand</span>
            </Button>
          </div>
        </DialogContentSection>

        <DialogMetaSection createdAt={match.createdAt} updatedAt={match.updatedAt} />
    </>
  );
};

/**
 * Form component that handles state, validation and submission logic for updating a match.
 */
const MatchEditForm = ({
  onSaveSuccess,
  match,
  listEntities,
  refetchEntities,
  setIsSaving,
  setCanSave,
}: {
  onSaveSuccess: () => void;
  match: MatchDetailsResponseDto;
  listEntities: any;
  refetchEntities: () => void;
  setIsSaving: (val: boolean) => void;
  setCanSave: (val: boolean) => void;
}) => {
  const { mutate: updateMatch } = useMatchesUpdate();
  const [isLoadingLocal, setIsLoadingLocal] = useState(false);
  const [isHomeGame, setIsHomeGame] = useState(
    match.homeOrAway === "AWAY" ? false : true
  );

  const setIsLoading = (val: boolean) => {
    setIsLoadingLocal(val);
    setIsSaving(val);
  };

  const isLoading = isLoadingLocal;

  // Quick Create Dialogs
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const [createOpponentOpen, setCreateOpponentOpen] = useState(false);

  const updateMatchSchema = z.object({
    belongsToTeamId: z.string().min(1, {
      message: "Mannschaft ist erforderlich",
    }),
    belongsToOpponentId: z.string().min(1, {
      message: "Gegner ist erforderlich",
    }),
    date: z.string().min(1, {
      message: "Datum ist erforderlich",
    }),
    location: z.string().min(1, {
      message: "Ort ist erforderlich",
    }),
    ourScore: z.number().nullable().optional(),
    opponentScore: z.number().nullable().optional(),
    leagueName: z.string().optional(),
  });

  type UpdateMatchSchema = z.infer<typeof updateMatchSchema>;

  const form = useForm<UpdateMatchSchema>({
    resolver: zodResolver(updateMatchSchema),
    defaultValues: {
      belongsToTeamId: match.belongsToTeamId,
      belongsToOpponentId: match.belongsToOpponentId || "",
      date: formatDateTimeForForm(match.date),
      location: match.location,
      ourScore: match.ourScore,
      opponentScore: match.opponentScore,
      leagueName: match.leagueName,
    },
  });

  const { isValid } = form.formState;

  useEffect(() => {
    setCanSave(isValid);
  }, [isValid, setCanSave]);

  const onSubmit = async (data: UpdateMatchSchema) => {
    setIsLoading(true);

    const promises = [];

    // Update match data
    const updatePromise = new Promise((resolve, reject) => {
      updateMatch(
        {
          id: match._id,
          data: {
            belongsToTeamId: data.belongsToTeamId,
            belongsToOpponentId: data.belongsToOpponentId,
            date: new Date(data.date).toISOString(),
            location: data.location,
            ourScore: data.ourScore,
            opponentScore: data.opponentScore,
            isWin:
              data.ourScore != null && data.opponentScore != null
                ? data.ourScore > data.opponentScore
                : undefined,
            homeOrAway: isHomeGame ? "HOME" : "AWAY",
            leagueName: data.leagueName,
          },
        },
        {
          onSuccess: resolve,
          onError: reject,
        }
      );
    });
    promises.push(updatePromise);

    try {
      await Promise.all(promises);
      toast.success("Spiel wurde erfolgreich aktualisiert");
      setIsLoading(false);
      onSaveSuccess();
    } catch (error) {
      toast.error("Spiel konnte nicht gespeichert werden", { error });
      setIsLoading(false);
    }
  };

  const opponents = listEntities?.opponents || [];
  const teams = listEntities?.teams || [];

  return (
    <>
      <Form {...form}>
        <form
          id="edit-match-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col flex-1 min-h-0 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
            {/* Home/Away Toggle */}
            <div className="flex justify-center pb-2">
              <Tabs
                value={isHomeGame ? "home" : "away"}
                onValueChange={(v) => setIsHomeGame(v === "home")}
                className="w-full max-w-md"
              >
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="home">Heimspiel</TabsTrigger>
                  <TabsTrigger value="away">Auswärtsspiel</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>

            <div className="flex gap-8 items-start">
              {/* Team Field - Always belongsToTeamId */}
              <div
                className={cn(
                  "w-1/2 transition-all duration-300 ease-in-out",
                  isHomeGame ? "order-1" : "order-2"
                )}
              >
                <FormField
                  control={form.control}
                  name="belongsToTeamId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1">
                      <EntitySelector
                        label={isHomeGame ? "Heim" : "Gast"}
                        entities={teams}
                        selectedId={field.value}
                        onSelect={field.onChange}
                        isLoading={isLoading}
                        placeholder="Mannschaft wählen..."
                        searchPlaceholder="Mannschaft suchen..."
                        onQuickCreate={() => setCreateTeamOpen(true)}
                        createLabel="Neue Mannschaft"
                      />
                      <FormMessage className="text-center" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Opponent Field - Always belongsToOpponentId */}
              <div
                className={cn(
                  "w-1/2 transition-all duration-300 ease-in-out",
                  isHomeGame ? "order-2" : "order-1"
                )}
              >
                <FormField
                  control={form.control}
                  name="belongsToOpponentId"
                  render={({ field }) => (
                    <FormItem className="flex flex-col gap-1">
                      <EntitySelector
                        label={isHomeGame ? "Gast" : "Heim"}
                        entities={opponents}
                        selectedId={field.value}
                        onSelect={field.onChange}
                        isLoading={isLoading}
                        placeholder="Gegner wählen..."
                        searchPlaceholder="Gegner suchen..."
                        onQuickCreate={() => setCreateOpponentOpen(true)}
                        createLabel="Neuer Gegner"
                      />
                      <FormMessage className="text-center" />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <FormField
              control={form.control}
              name="leagueName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel isOptional={false}>
                    <Trophy className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                    Wettbewerb
                  </FormLabel>
                  <FormControl>
                    <Autocomplete
                      options={listEntities?.leagueRecommendations || []}
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="z.B. Kreisliga A"
                      emptyText="Keine Vorschläge gefunden. Der neue Wettbewerb wird übernommen."
                      disabled={isLoading}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <Calendar className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Datum
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="2025-01-01"
                        {...field}
                        disabled={isLoading}
                        type="datetime-local"
                        className="h-11"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel isOptional={false}>
                      <MapPin className="w-3.5 h-3.5 mr-2 inline-block text-muted-foreground" />
                      Ort
                    </FormLabel>
                    <FormControl>
                      <Autocomplete
                        options={listEntities?.locationRecommendations || []}
                        value={field.value || ""}
                        onChange={field.onChange}
                        placeholder="z.B. Hauptplatz"
                        emptyText="Keine Vorschläge gefunden. Der neue Ort wird übernommen."
                        disabled={isLoading}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFormSection title="Ergebnis">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="ourScore"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                        Unsere Tore
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="0"
                          {...field}
                          value={field.value ?? ""}
                          disabled={isLoading}
                          type="number"
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                          className="bg-white text-center font-bold text-lg h-12"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="opponentScore"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs uppercase tracking-wider text-muted-foreground">
                        Gegner Tore
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="0"
                          {...field}
                          value={field.value ?? ""}
                          type="number"
                          disabled={isLoading}
                          onChange={(e) =>
                            field.onChange(
                              e.target.value === ""
                                ? null
                                : Number(e.target.value)
                            )
                          }
                          className="bg-white text-center font-bold text-lg h-12"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </DialogFormSection>
          </div>
        </form>
      </Form>

      {/* Quick Create Dialogs */}
      {createTeamOpen && (
        <CreateTeamDialog
          open={createTeamOpen}
          onClose={() => setCreateTeamOpen(false)}
          onCreated={(team) => {
            refetchEntities();
            form.setValue("belongsToTeamId", team._id);
            setCreateTeamOpen(false);
          }}
        />
      )}

      {createOpponentOpen && (
        <CreateOpponentDialog
          open={createOpponentOpen}
          onClose={() => setCreateOpponentOpen(false)}
          onCreated={(opponent) => {
            refetchEntities();
            form.setValue("belongsToOpponentId", opponent._id);
            setCreateOpponentOpen(false);
          }}
        />
      )}
    </>
  );
};

export default MatchDetailsDialog;
